"use client";

import { useId } from "react";

import NavAnchor, { isHashHref } from "@/components/nav/NavAnchor";
import { NAV_GROUP_EYEBROW, panelRowClass } from "@/components/nav/nav-styles";
import { IconMapping } from "@/components/Sidebar/IconMapping";
import { KeyCombo } from "@/components/ui/kbd";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useApplePlatform } from "@/hooks/useApplePlatform";
import { pageShortcutHelp, pageShortcutKeys } from "@/lib/keyboard";
import { stripNavigationLocalePrefix } from "@/lib/navigation/is-nav-item-active";
import { cn } from "@/lib/utils";

/**
 * The links inside the side nav's panel, in the manifest's vocabulary
 * (`lib/navigation/nav-manifest.js`): a top-level entry with `subItems` and no
 * `href` is a group heading; an entry with an `href` is a link, and a link may
 * carry its own `subItems` (`label`, `href`, optional `count`), always listed
 * beneath it — the playbook's sections under the Playbook link, as the mobile
 * drawer lists them. A link with a `shortcut` shows its keys and describes
 * them to assistive technology without changing its name.
 *
 * Which link is current comes from the route unless `activeHrefs` is given
 * (the playbook's scroll spy, whose links are in-page anchors). That outline
 * is the one place sub-items fold away: a section's subheadings are listed
 * only while that section is being read, so the outline follows the reader
 * instead of listing every heading on the page.
 */

const visible = (items) =>
  (items || []).filter((item) => item && item.display !== false);

// An item names its icon by IconMapping key (`iconName`, the manifest) or as a
// component (`icon`, the playbook outline, whose section headings use the same
// heroicons so the panel and the page agree).
function ItemIcon({ icon, iconName, className }) {
  const Icon = icon || IconMapping[iconName];
  return Icon ? <Icon className={className} strokeWidth={1.75} /> : null;
}

/**
 * Which href in `items` the viewer is on: the longest route the pathname is at
 * or under, where an anchor beats its page only while its hash is current.
 * Matching stops at a path segment, so `/dashboards` is not under `/dashboard`.
 */
export function resolveCurrentHref(items, pathname, currentHash = "") {
  const path = stripNavigationLocalePrefix(pathname || "");
  const hash = (currentHash || "").replace(/^#/, "");
  let best = null;
  let bestScore = -1;

  const consider = (item) => {
    if (item.external || typeof item.href !== "string") return;
    const [target, targetHash = ""] = stripNavigationLocalePrefix(
      item.href,
    ).split("#", 2);
    const route = target || path; // "#id" means this page
    const under =
      path === route ||
      (route !== "/" &&
        path.startsWith(route.endsWith("/") ? route : `${route}/`));
    if (!under || (targetHash && targetHash !== hash)) return;

    const score = route.length + (targetHash ? 1 + targetHash.length : 0);
    if (score > bestScore) {
      best = item.href;
      bestScore = score;
    }
  };
  const walk = (list) =>
    visible(list).forEach((item) => {
      consider(item);
      walk(item.subItems);
    });
  walk(items);
  return best;
}

/** Keep the shortcut out of the link's name but available as its description. */
function ShortcutAnchor({ help, children, ...props }) {
  const helpId = useId();
  return (
    <NavAnchor {...props} aria-describedby={help ? helpId : undefined}>
      {children}
      {help && (
        <span id={helpId} className="sr-only" aria-hidden="true">
          {help}
        </span>
      )}
    </NavAnchor>
  );
}

// The keys beside a link. The help is already the link's description, so the
// tooltip repeats it for sighted readers only.
function ShortcutBadge({ letter, apple, help }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="ml-auto inline-flex shrink-0" aria-hidden="true">
          <KeyCombo keys={pageShortcutKeys(letter, apple)} />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-64 font-normal" aria-hidden="true">
        {help}
      </TooltipContent>
    </Tooltip>
  );
}

function Row({ item, active, apple, onNavigate }) {
  const help =
    item.shortcut && !item.external
      ? pageShortcutHelp(item.shortcut, item.label, apple)
      : undefined;
  return (
    <ShortcutAnchor
      href={item.href}
      active={active}
      external={item.external}
      help={help}
      onClick={() => onNavigate(item.href)}
      className={panelRowClass(active)}
    >
      <span className="flex h-5 w-5 shrink-0 items-center justify-center">
        <ItemIcon
          icon={item.icon}
          iconName={item.iconName}
          className="h-[18px] w-[18px]"
        />
      </span>
      <span className="truncate">{item.label}</span>
      {help && (
        <ShortcutBadge letter={item.shortcut} apple={apple} help={help} />
      )}
    </ShortcutAnchor>
  );
}

// The entries under one link, indented on a thin rail so they read as part of
// that link rather than as more links.
function NestedItems({ items, isActive, onNavigate }) {
  return (
    <ul
      className="my-1 ml-[29px] space-y-px border-l border-stone-200 pl-2"
      data-testid="sidebar-nested-items"
    >
      {items.map((item) => {
        const active = isActive(item);
        return (
          <li key={item.href} className="relative">
            {active && (
              <span
                aria-hidden="true"
                className="absolute -left-[9px] top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-full bg-brand-800"
              />
            )}
            <NavAnchor
              href={item.href}
              active={active}
              onClick={() => onNavigate(item.href)}
              className={cn(
                "flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-[13px] leading-5 transition-colors duration-150",
                active
                  ? "font-medium text-brand-800"
                  : "text-gray-500 hover:bg-stone-200/50 hover:text-gray-900",
              )}
            >
              <span className="truncate">{item.label}</span>
              {typeof item.count === "number" && (
                <span className="shrink-0 text-[11px] tabular-nums text-gray-400">
                  {item.count}
                </span>
              )}
            </NavAnchor>
          </li>
        );
      })}
    </ul>
  );
}

export default function NavPanelItems({
  items,
  pathname,
  currentHash = "",
  activeHrefs,
  onNavigateHash,
}) {
  const apple = useApplePlatform();
  const currentHref = activeHrefs
    ? null
    : resolveCurrentHref(items, pathname, currentHash);
  const isActive = (item) =>
    activeHrefs
      ? activeHrefs.includes(item.href)
      : Boolean(item.href) && item.href === currentHref;

  // A route-plus-hash link (`/playbook#glossary`) goes through the client
  // router, which fires no `hashchange`, so the playbook is told to open the
  // section by event. A bare `#id` is a plain anchor and needs neither.
  const onNavigate = (href = "") => {
    const hashIndex = href.indexOf("#");
    const sectionId = hashIndex === -1 ? "" : href.slice(hashIndex + 1);
    onNavigateHash?.(sectionId);
    if (!sectionId || isHashHref(href)) return;
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("openPlaybookSection", { detail: { sectionId } }),
      );
    }, 100);
  };

  const renderLeaf = (item) => {
    // An entry with no href is a group that lost every child; never a link.
    if (!item.href) return null;
    const active = isActive(item);
    const nested = visible(item.subItems);
    const showNested =
      nested.length > 0 && (!activeHrefs || active || nested.some(isActive));
    return (
      <div key={item.href}>
        <Row item={item} active={active} apple={apple} onNavigate={onNavigate} />
        {showNested && (
          <NestedItems
            items={nested}
            isActive={isActive}
            onNavigate={onNavigate}
          />
        )}
      </div>
    );
  };

  return visible(items).map((item, index) => {
    const subs = visible(item.subItems);
    if (item.href) return renderLeaf(item);
    if (subs.length === 0) return null;
    return (
      <div
        key={item.label}
        className={cn(index > 0 && "mt-3 border-t border-stone-200/70 pt-3")}
      >
        <div className={NAV_GROUP_EYEBROW}>{item.label}</div>
        <div className="space-y-0.5">{subs.map(renderLeaf)}</div>
      </div>
    );
  });
}
